import {
  type BalanceBuyerResult,
  type BalanceCreditCard,
  type PayMethod,
  PayMethodTypeEnum,
} from '@b-stock/payments-methods-api-client'

import {
  asTypedPaymentMethod,
  type CardPaymentDetails,
  type BankPaymentDetails,
} from '../shared'
import type { Details } from './PaymentMethodCard/PaymentMethodCard'

export const getDetails = (method: PayMethod): Details => {
  const typedPayMethod = asTypedPaymentMethod(method)

  if (typedPayMethod.type === PayMethodTypeEnum.Card) {
    const cardDetails = typedPayMethod.details as CardPaymentDetails
    const { last4, expMonth, expYear, brand } = cardDetails.card
    return { last4, expMonth, expYear, brand }
  }

  if (
    typedPayMethod.type === PayMethodTypeEnum.Bank ||
    typedPayMethod.type === PayMethodTypeEnum.StripeFinConn
  ) {
    const bankDetails = typedPayMethod.details as BankPaymentDetails
    return {
      last4: bankDetails.last4,
      ...(bankDetails.us_bank_account && {
        us_bank_account: bankDetails.us_bank_account,
      }),
    }
  }

  if (typedPayMethod.type === PayMethodTypeEnum.GetbalanceCc) {
    const cardDetails = typedPayMethod.details as BalanceBuyerResult
    // Newest credit card is at the beginning of the array
    const creditCard: BalanceCreditCard | undefined =
      cardDetails.paymentData?.creditCards?.[0]
    return {
      last4: creditCard?.last4 ?? '',
      expMonth: creditCard?.expiredMonth ?? 0,
      expYear: creditCard?.expiredYear ?? 0,
      brand: creditCard?.brand ?? '',
    }
  }

  // fallback for other payment types
  return typedPayMethod.details as Details
}

export const getOwner = (method: PayMethod): string => {
  const unknown = 'Unknown'
  const typedPayMethod = asTypedPaymentMethod(method)

  if (typedPayMethod.type === PayMethodTypeEnum.Card) {
    const cardDetails = typedPayMethod.details as CardPaymentDetails
    return (
      cardDetails.owner?.name || cardDetails.billingDetails?.name || unknown
    )
  }

  if (
    typedPayMethod.type === PayMethodTypeEnum.Bank ||
    typedPayMethod.type === PayMethodTypeEnum.StripeFinConn
  ) {
    const bankDetails = typedPayMethod.details as BankPaymentDetails
    return bankDetails.us_bank_account?.bank_name || unknown
  }

  if (typedPayMethod.type === PayMethodTypeEnum.GetbalanceCc) {
    const cardDetails = typedPayMethod.details as BalanceBuyerResult
    return `${cardDetails.firstName} ${cardDetails.lastName}`
  }

  return unknown
}

export const getMethodIdentifier = (method: PayMethod) => {
  const typedPayMethod = asTypedPaymentMethod(method)

  if (typedPayMethod.type === PayMethodTypeEnum.Card) {
    const cardDetails = typedPayMethod.details as CardPaymentDetails
    return {
      type: typedPayMethod.type,
      last4: cardDetails.card.last4,
      brand: cardDetails.card.brand,
    }
  }

  if (
    typedPayMethod.type === PayMethodTypeEnum.Bank ||
    typedPayMethod.type === PayMethodTypeEnum.StripeFinConn
  ) {
    const bankDetails = typedPayMethod.details as BankPaymentDetails
    return {
      type: typedPayMethod.type,
      last4: bankDetails.last4 || bankDetails.us_bank_account?.last4 || '',
      bankName: bankDetails.us_bank_account?.bank_name || '',
    }
  }

  if (typedPayMethod.type === PayMethodTypeEnum.GetbalanceCc) {
    const cardDetails = typedPayMethod.details as BalanceBuyerResult
    const creditCard: BalanceCreditCard | undefined =
      cardDetails.paymentData?.creditCards?.[0]
    return {
      type: typedPayMethod.type,
      last4: creditCard?.last4 ?? '',
      brand: creditCard?.brand ?? '',
    }
  }

  // fallback for other payment types
  const otherPayMethodDetails = typedPayMethod.details as Record<
    string,
    unknown
  >
  return {
    type: typedPayMethod.type,
    last4: otherPayMethodDetails.last4 as string,
  }
}

export const getTypeLabel = (method: PayMethod): string => {
  const typedPayMethod = asTypedPaymentMethod(method)
  if (
    typedPayMethod.type === PayMethodTypeEnum.Card ||
    typedPayMethod.type === PayMethodTypeEnum.GetbalanceCc
  ) {
    return 'Card'
  }
  if (
    typedPayMethod.type === PayMethodTypeEnum.Bank ||
    typedPayMethod.type === PayMethodTypeEnum.StripeFinConn
  ) {
    return 'Bank'
  }
  return typedPayMethod.type
}

/**
 * True for credit-card-backed methods: Stripe's and Balance's alike.
 */
export const isCreditCardMethod = (type: PayMethod['type']): boolean =>
  type === PayMethodTypeEnum.Card || type === PayMethodTypeEnum.GetbalanceCc

/**
 * True for ACH bank-transfer methods.
 */
export const isAchMethod = (type: PayMethod['type']): boolean =>
  type === PayMethodTypeEnum.Bank || type === PayMethodTypeEnum.StripeFinConn

/**
 * Restriction semantics used by PaymentMethodCard styling:
 * - 'ach_unavailable': opt-in neutral ACH treatment for the payment picker
 * - 'account_restricted': tan + badge
 * - 'listing_cc_disabled': gray only
 */
export type PaymentRestrictionReason =
  | 'ach_unavailable'
  | 'account_restricted'
  | 'listing_cc_disabled'

export const getPaymentRestrictionReason = (
  type: PayMethod['type'],
  restrictions: {
    isACHBlocked: boolean
    isCreditCardBlockedByAccount: boolean
    isCreditCardDisabledByListing: boolean
  }
): PaymentRestrictionReason | undefined => {
  if (isCreditCardMethod(type)) {
    if (restrictions.isCreditCardBlockedByAccount) return 'account_restricted'
    if (restrictions.isCreditCardDisabledByListing) return 'listing_cc_disabled'
    return undefined
  }
  if (
    (type === PayMethodTypeEnum.Bank ||
      type === PayMethodTypeEnum.StripeFinConn) &&
    restrictions.isACHBlocked
  ) {
    return 'account_restricted'
  }
  return undefined
}

type DisabledPaymentConfig = {
  ach?: boolean
  card?: boolean
  netTerms?: boolean
}

export const isPaymentMethodDisabled = (
  type: PayMethod['type'],
  config: DisabledPaymentConfig
): boolean => {
  switch (type) {
    case PayMethodTypeEnum.StripeFinConn:
    case PayMethodTypeEnum.Bank:
      return config.ach ?? false
    case PayMethodTypeEnum.GetbalanceCc:
    case PayMethodTypeEnum.Card:
      return config.card ?? false
    case PayMethodTypeEnum.Getbalance:
      return config.netTerms ?? false
    default:
      return false
  }
}
